import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronLeft, Shield, User, Users, Heart } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roles = [
  {
    id: "coach",
    title: "Тренер",
    description: "Управление расписанием и командой",
    icon: Shield,
    color: "#1E4E3D"
  },
  {
    id: "athlete",
    title: "Спортсмен",
    description: "Отслеживание тренировок и статистики",
    icon: User,
    color: "#007AFF"
  },
  {
    id: "parent",
    title: "Родитель",
    description: "Наблюдение за прогрессом ребенка",
    icon: Heart,
    color: "#FF9500"
  },
  {
    id: "amateur",
    title: "Любитель",
    description: "Участие в общении команды",
    icon: Users,
    color: "#34C759"
  }
];

export default function CompleteProfilePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = location.state?.user;
  
  const [formData, setFormData] = useState({
    firstName: user?.first_name || "",
    lastName: user?.last_name || "",
    role: user?.role || "athlete"
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!formData.firstName || !formData.lastName) {
      toast.error("Заполните имя и фамилию");
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      
      // Update user profile
      const response = await axios.put(`${API}/auth/complete-profile`, {
        first_name: formData.firstName,
        last_name: formData.lastName,
        role: formData.role
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      localStorage.setItem("user", JSON.stringify(response.data));
      navigate("/home");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка сохранения профиля");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="complete-profile-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-100/50 px-4 py-4">
        <div className="flex items-center">
          <h1 className="font-heading text-xl font-semibold">Завершите регистрацию</h1>
        </div>
      </header>

      {/* Content */}
      <div className="px-6 py-8">
        <p className="text-apple-text-secondary mb-6 text-center">
          Укажите ваши данные для отображения в чате
        </p>

        {/* Name Fields */}
        <div className="space-y-4 mb-8">
          <div className="space-y-2">
            <Label htmlFor="firstName" className="text-sm font-medium text-apple-text-secondary">
              Имя
            </Label>
            <Input
              id="firstName"
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              placeholder="Иван"
              className="h-12 bg-apple-gray border-transparent focus:bg-white focus:border-golf-green/20 focus:ring-2 focus:ring-golf-green/10 rounded-xl"
              data-testid="input-firstname"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName" className="text-sm font-medium text-apple-text-secondary">
              Фамилия
            </Label>
            <Input
              id="lastName"
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              placeholder="Иванов"
              className="h-12 bg-apple-gray border-transparent focus:bg-white focus:border-golf-green/20 focus:ring-2 focus:ring-golf-green/10 rounded-xl"
              data-testid="input-lastname"
            />
          </div>
        </div>

        {/* Role Selection */}
        <p className="text-sm font-medium text-apple-text-secondary mb-3">Выберите роль</p>
        <div className="space-y-3 mb-8">
          {roles.map((role) => {
            const Icon = role.icon;
            const isSelected = formData.role === role.id;
            
            return (
              <button
                key={role.id}
                onClick={() => setFormData({ ...formData, role: role.id })}
                className={`role-card w-full p-4 rounded-2xl border-2 transition-all text-left flex items-center gap-3 ${
                  isSelected 
                    ? "border-golf-green bg-golf-green/5" 
                    : "border-gray-100 bg-white hover:border-gray-200"
                }`}
                data-testid={`role-${role.id}`}
              >
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${role.color}15` }}
                >
                  <Icon 
                    className="w-6 h-6" 
                    style={{ color: role.color }}
                  />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-semibold text-black">
                    {role.title}
                  </h3>
                  <p className="text-apple-text-secondary text-sm">
                    {role.description}
                  </p>
                </div>
                <div 
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                    isSelected 
                      ? "border-golf-green bg-golf-green" 
                      : "border-gray-300"
                  }`}
                >
                  {isSelected && (
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Save Button */}
        <Button
          onClick={handleSubmit}
          disabled={loading || !formData.firstName || !formData.lastName}
          className="w-full h-14 bg-golf-green hover:bg-golf-green-hover text-white rounded-full text-lg font-semibold"
          data-testid="save-profile-btn"
        >
          {loading ? "Сохранение..." : "Сохранить"}
        </Button>
      </div>
    </div>
  );
}
